import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import {
	getIsCollabOffline,
	subscribeToCollabNetworkStatus,
} from '@confluence/collab-network-status';

import { getTaskManager } from './TaskManager.ts';
import { preloadIfNeeded } from './preloader.ts';
import type { DefinitionType, ModuleType, LoadableType, LoadableExportType } from './types.ts';
import { LoadingPriority } from './constants.ts';
import { JS_LOAD_START, JS_LOAD_END } from './loadablePerformanceMarks.ts';
import { hasBeenLoaded, saveHasBeenLoaded } from './loadStateCache.ts';
import { createLoadableClientComponent } from './createLoadableClientComponent.tsx';
import { createLoadableServerComponent } from './createLoadableServerComponent.tsx';
import { createSuspenseClientComponent } from './createLoadableSuspenseClientComponent.tsx';
import { createSuspenseServerComponent } from './createLoadableSuspenseServerComponent.tsx';
import { shouldForceImportsForJest } from './shouldForceImportsForJest.ts';

const logger = getLogger('Loadable');

function markLoadingStart(loadableName: string, priority: LoadingPriority) {
	const startMark = `${loadableName}(${priority})${JS_LOAD_START}`;

	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		priority <= LoadingPriority.AFTER_PAINT &&
		performance?.getEntriesByName?.(startMark)?.length === 0
	) {
		performance.mark(startMark);
	}
}

function markLoadingEnd(loadableName: string, priority: LoadingPriority) {
	const endMark = `${loadableName}(${priority})${JS_LOAD_END}`;

	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		priority <= LoadingPriority.AFTER_PAINT &&
		performance?.getEntriesByName?.(endMark)?.length === 0
	) {
		performance.mark(endMark);

		if (process.env.NODE_ENV !== 'production') {
			performance.measure(
				`${loadableName}(${priority})`,
				`${loadableName}(${priority})${JS_LOAD_START}`,
				endMark,
			);
		}
	}
}

/**
 * Wraps a promise-returning function with a network online check.
 * If the network is online, it returns the function as is.
 * If the network is offline, it returns a new function that waits for the network to come online before calling the original function.
 * @param fn - The function to wrap.
 * @returns A new function that checks the network status before calling the original function.
 * @internal
 */
function wrapWithNetworkOnlineCheck<T>(fn: () => Promise<T>): () => Promise<T> {
	const isOnline = !getIsCollabOffline();

	if (isOnline) {
		// If online, no delay is needed
		return fn;
	}

	return () =>
		new Promise<T>((resolve) => {
			const currentlyOnline = !getIsCollabOffline();

			if (currentlyOnline) {
				// We're double-checking here in case the network status changes between initialising this callback and running it
				resolve(fn());
			} else {
				// Subscribe to network status changes – detect when the network comes back online
				const unsubscribe = subscribeToCollabNetworkStatus(() => {
					if (!getIsCollabOffline()) {
						resolve(fn());
						unsubscribe();
					}
				});
			}
		});
}

function evaluateBooleanOrPredicate(condition: boolean | (() => boolean)): boolean {
	if (typeof condition === 'function') {
		return condition();
	}
	return condition;
}

export function createLoadable<P extends {}>(priority: number) {
	return (definition: DefinitionType<P>): LoadableType<P> => {
		const {
			__loadable_id__,
			_maybeSyncLoader,
			loader,
			name,
			waitForNetworkOnline = false,
		} = definition;
		const loadableName = name || __loadable_id__ || 'unknown';
		const displayName = `Loadable(${loadableName})`;
		const loadedModuleRef: { ref: ModuleType<P> } = { ref: null };
		const totalHydratedAtOnce = 10;
		/**
		 * DON'T CACHE THE LOADER RESULT OR PROMISE
		 * There are quite a few places that calls function defined in outer scope like packages/confluence-fabric-editor/src/components/EditorLoader/Loader.tsx
		 * There is no guarantee they won't change and loader result is consistent. Caching the result might lead to bugs.
		 * We can theoretically run babel plugin to find out these cases and exclude them https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence-frontend/commits/6ab47236eec2fa7c4a7542ad05b8c45964443ba1
		 * However I don't feel worth the effort of maintaining another piece of logic in babel plugin and risk of missing out the side effects in loader function.
		 * So I am leaving it as it is.
		 */
		const cleanup = () => {
			markLoadingEnd(loadableName, priority);
			saveHasBeenLoaded(__loadable_id__);
		};
		const runLoader = (): Promise<ModuleType<P> | Error> => {
			markLoadingStart(loadableName, priority);

			const handleResolve = (module: any) => {
				cleanup();
				return (loadedModuleRef.ref = (module as any)?.default || module);
			};

			const handleError = (e: Error) => {
				cleanup();
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					let extra: string[] = [];
					if (e.message.includes('randomBytes')) {
						extra = [
							'==IMPORTANT==',
							"It seems like you are using 'randomBytes' or `uuid` please make sure they are called lazily.",
							'This means the function call should not be executed when the module is imported by another module.',
							'This is because the SSR environment pre-compiles all the modules.',
							"During pre-compile (or we call init) phase it doesn't have access to Nodejs libraries like 'crypto'.",
							'It is OK to call them in the running phase, eg: in side React component',
						];
					}

					const error = new Error();
					// Only stack is logged so adding the extra message to stack
					error.stack =
						[
							`Error when calling load() of "${loadableName}" in SSR App bundle's init().`,
							...extra,
							'Please check with #cc-fe-performance if you need help with this.\n',
						].join('\n') + e.stack;
					throw error;
				} else {
					if (fg('confluence_frontend_surface_loadable_errors')) {
						getMonitoringClient().submitError(e, {
							attribution: 'backbone-loadable',
						});
						logger.error`Error loading loadable component ${loadableName}: ${e.message}. Please check with #cc-fe-performance if you need help with this.\n`;
					}
					return e;
				}
			};

			if (_maybeSyncLoader) {
				try {
					// Unfortunately for sync loader we lose the ability to make it aware of network status.
					// However good news is if loader was made sync it means it is definitely not a network call.
					const result = loader();
					// It is maybe sync so it can still return a promise.
					if (result instanceof Promise) {
						return result.then(handleResolve, handleError);
					} else {
						// Still return a promised result to keep everywhere else happy.
						// But we have saved the module to the ref. Everything should work fine. :)
						return Promise.resolve(handleResolve(result));
					}
				} catch (e) {
					return Promise.resolve(handleError(e));
				}
			} else {
				const loaderWithNetworkOnline =
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					evaluateBooleanOrPredicate(waitForNetworkOnline) && !process.env.REACT_SSR
						? wrapWithNetworkOnlineCheck(loader)
						: loader;

				// At the time when a promise is rejected, it must already been attached with a catch.
				// However it is not the case when running in the task manager.
				// Because a task can return a promise or a value. We have to check before attaching a catch.
				// This makes the rejection unhandled. So we are converting it to a resolved promise with an error.
				// The error object will be handled later in LoadableComponent.
				return loaderWithNetworkOnline().then(handleResolve, handleError);
			}
		};

		const load = (
			priorityOverride: number = -1,
			isHydrating: boolean = false,
		): Promise<ModuleType<P> | Error> => {
			if (loadedModuleRef.ref) {
				return Promise.resolve(loadedModuleRef.ref);
			}

			if (
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				process.env.REACT_SSR ||
				hasBeenLoaded(__loadable_id__) ||
				_maybeSyncLoader ||
				shouldForceImportsForJest()
			) {
				return runLoader();
			}

			return getTaskManager<ModuleType<P> | Error>({
				totalHydratedAtOnce,
			}).push({
				id: __loadable_id__!,
				priority: priorityOverride >= LoadingPriority.PAINT ? priorityOverride : priority,
				task: runLoader,
				_delayHydration: isHydrating,
			});
		};

		if (_maybeSyncLoader || shouldForceImportsForJest()) {
			void load();
		} else {
			/*
			 * Preload dynamic split when:
			 * - In React SSR mode
			 * - id of the component is in the magical __LOADABLE__ array
			 * Note: __LOADABLE__ array is produced by React SSR server where server knows what loadable has been used on the server side.
			 * Client will preload everything in __LOADABLE__ so component do not flip back to the loading state.
			 *
			 * Note: __SSR_RENDERED__ is not checked here because we initialize loadable very early that is before SSR full result.
			 */
			preloadIfNeeded(__loadable_id__, name, () => load(LoadingPriority.PAINT));
		}

		if (priority === LoadingPriority.ACTIVATABLE) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				return createSuspenseServerComponent(definition, {
					displayName,
					loadableName,
					priority,
					load,
					loadedModuleRef,
				});
			} else {
				return createSuspenseClientComponent(definition, {
					displayName,
					loadableName,
					priority,
					load,
					loadedModuleRef,
				});
			}
		}

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return createLoadableServerComponent(definition, {
				displayName,
				loadableName,
				priority,
				load,
				loadedModuleRef,
			});
		}

		return createLoadableClientComponent(definition, {
			displayName,
			loadableName,
			priority,
			load,
			loadedModuleRef,
		});
	};
}

/** https://pug.jira-dev.com/wiki/spaces/CFE/blog/20778976578/Introducing%2Bphased%2Bloadable */
export const LoadablePaint: LoadableExportType = createLoadable(LoadingPriority.PAINT);

/** https://pug.jira-dev.com/wiki/spaces/CFE/blog/20778976578/Introducing%2Bphased%2Bloadable */
export const LoadableAfterPaint: LoadableExportType = createLoadable(LoadingPriority.AFTER_PAINT);

/** https://pug.jira-dev.com/wiki/spaces/CFE/blog/20778976578/Introducing%2Bphased%2Bloadable */
export const LoadableLazy: LoadableExportType = createLoadable(LoadingPriority.LAZY);

/** https://pug.jira-dev.com/wiki/spaces/CFE/blog/20778976578/Introducing%2Bphased%2Bloadable */
export const LoadableBackground: LoadableExportType = createLoadable(LoadingPriority.BACKGROUND);

export const LoadableHydrateOnHover: LoadableExportType = createLoadable(
	LoadingPriority.HYDRATE_ON_HOVER,
);

export const LoadableActivatable: LoadableExportType = createLoadable(LoadingPriority.ACTIVATABLE);
